export interface LiveCallEntry {
  owner_uid?: string;
  person_uid?: string;
  report_name?: string;
  call_type?: string;
  state: string;
  reference_id?: string | null;
  room_name?: string | null;
  conversation_id?: string | null;
  participant_phone?: string | null;
  recording_url?: string | null;
  target_id?: string | null;
  updated_at?: string | null;
  expires_at?: string | null;
}

export interface LiveCallLookup {
  byReference: Record<string, LiveCallEntry>;
  byPhone: Record<string, LiveCallEntry>;
  byRoom: Record<string, LiveCallEntry>;
}

const TERMINAL_STATES = new Set(["completed", "failed", "declined"]);

export function normalizePhone(value?: string | null): string {
  if (!value) {
    return "";
  }
  return value.replace(/[^+\d]/g, "");
}

export function sortLiveCallEntries(entries: LiveCallEntry[]): LiveCallEntry[] {
  return [...entries].sort((a, b) => {
    const aTime = Date.parse(a.updated_at || "") || 0;
    const bTime = Date.parse(b.updated_at || "") || 0;
    return bTime - aTime;
  });
}

export function pruneLiveCallEntries(
  entries: LiveCallEntry[],
  now: number = Date.now()
): LiveCallEntry[] {
  return entries.filter((entry) => {
    if (!entry.expires_at) {
      return true;
    }
    const expiresAt = Date.parse(entry.expires_at);
    if (!expiresAt) {
      return true;
    }
    return expiresAt > now;
  });
}

export function upsertLiveCallEntry(
  entries: LiveCallEntry[],
  nextEntry: LiveCallEntry
): LiveCallEntry[] {
  const byId = sanitizeId(nextEntry.reference_id);
  const byRoom = sanitizeId(nextEntry.room_name);
  const byConversation = sanitizeId(nextEntry.conversation_id);
  const phoneKey = phoneLookupKey(nextEntry);

  const index = entries.findIndex((entry) => {
    if (byId && sanitizeId(entry.reference_id) === byId) {
      return true;
    }
    if (byRoom && sanitizeId(entry.room_name) === byRoom) {
      return true;
    }
    if (
      byConversation &&
      sanitizeId(entry.conversation_id) === byConversation
    ) {
      return true;
    }
    if (phoneKey && phoneLookupKey(entry) === phoneKey) {
      return true;
    }
    return false;
  });

  const clone = [...entries];
  if (index >= 0) {
    clone[index] = nextEntry;
  } else {
    clone.unshift(nextEntry);
  }
  return sortLiveCallEntries(clone);
}

export function buildLiveCallLookup(entries: LiveCallEntry[]): LiveCallLookup {
  const lookup: LiveCallLookup = {
    byReference: {},
    byPhone: {},
    byRoom: {},
  };

  entries.forEach((entry) => {
    const refId = sanitizeId(entry.reference_id);
    if (refId) {
      lookup.byReference[refId] = entry;
    }
    const room = sanitizeId(entry.room_name);
    if (room) {
      lookup.byRoom[room] = entry;
    }
    const phoneKey = phoneLookupKey(entry);
    if (phoneKey) {
      lookup.byPhone[phoneKey] = entry;
    }
  });

  return lookup;
}

export function matchLiveCallForReference(
  reference: Record<string, any>,
  lookup: LiveCallLookup,
  fallbackCallType: string
): LiveCallEntry | null {
  const referenceId = sanitizeId(
    reference.reference_id || reference.referenceId || reference.id
  );
  if (referenceId && lookup.byReference[referenceId]) {
    return lookup.byReference[referenceId];
  }

  const roomName = sanitizeId(reference.room_name || reference.roomName);
  if (roomName && lookup.byRoom[roomName]) {
    return lookup.byRoom[roomName];
  }

  const normalizedPhone = normalizePhone(
    reference.reference_phone || reference.phone
  );
  if (normalizedPhone) {
    const phoneKey = `${fallbackCallType}:${normalizedPhone}`;
    if (lookup.byPhone[phoneKey]) {
      return lookup.byPhone[phoneKey];
    }
  }

  return null;
}

export function isActiveState(state: string | undefined): boolean {
  if (!state) {
    return false;
  }
  const normalized = state.toLowerCase();
  return !TERMINAL_STATES.has(normalized);
}

export function pickRelevantLiveCall(
  entries: LiveCallEntry[]
): LiveCallEntry | null {
  if (!entries || !entries.length) {
    return null;
  }
  const ordered = sortLiveCallEntries(entries);
  const active = ordered.find((entry) => isActiveState(entry.state));
  return active || ordered[0];
}

function sanitizeId(value?: string | null): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  const trimmed = `${value}`.trim();
  return trimmed || null;
}

function phoneLookupKey(entry: LiveCallEntry): string | null {
  if (!entry.participant_phone) {
    return null;
  }
  const phone = normalizePhone(entry.participant_phone);
  if (!phone) {
    return null;
  }
  return `${entry.call_type || "reference"}:${phone}`;
}
